/**
 * Temporary screen-scoped design tokens for **Reports** (`/reports`).
 *
 * Mirrors the analytics-table half of `Reports.dc.html` — every colour, size,
 * radius, spacing and type value the page chrome needs lives here so the
 * components never inline a literal. See UI_IMPLEMENTATION_RULES.md § Temporary
 * Screen Tokens.
 *
 * This deliberately stops at the page-level chrome (title, tabs). Each tab's
 * own search / date-range / download toolbar reuses the app's existing inline
 * pattern (see `UserRequestAnalyticsReport.tsx`) and `reportsToolbarStyles.ts`'s
 * theme-token-based download button, rather than screen-token hex values.
 * `CustomTable` owns the table itself — its header, row and selection colours
 * come from `theme.palette.table.*` (see `CustomTable.tokens.ts`), so
 * re-skinning a table means changing that palette for every table at once,
 * not hardcoding a one-off here.
 */

import { button, contentCard, formField, inkfree, signer, white } from "theme/colors";

export const reportsTokens = {
  /** "Reports" title block. */
  header: {
    titleColor: signer.ink,
    titleFontSize: "26px",
    titleFontWeight: 800,
    titleLetterSpacing: "-.02em",
    subtitleColor: signer.text.subtitle,
    subtitleFontSize: "13.5px",
    subtitleMarginTop: "6px",
  },

  /**
   * Search ▸ (date range) ▸ Download row above the table.
   * `Reports.dc.html` § ANALYTICS TABLES.
   */
  toolbar: {
    gap: "14px",
    marginBottom: "20px",
    search: {
      bg: white,
      border: formField.borderWarm,
      radius: "12px",
      padding: "13px 16px",
      gap: "11px",
      fontSize: "14px",
      color: signer.ink,
      placeholder: formField.placeholderMuted,
      iconSize: 17,
      iconColor: signer.text.faint,
      focusBorder: signer.orange.main,
    },
    download: {
      height: 48,
      paddingX: "20px",
      radius: "12px",
      gap: "9px",
      fontSize: "13.5px",
      fontWeight: 700,
      iconSize: 16,
      enabledBg: inkfree.main,
      enabledHoverBg: inkfree.dark,
      enabledColor: white,
      disabledBg: button.disabledBg,
      disabledColor: button.disabledColor,
      disabledBorder: contentCard.borderMuted,
    },
  },

  /**
   * Keyboard focus indicator for the tabs. `ButtonBase` resets the browser's
   * own outline, so the tab has to draw one (WCAG 2.4.7). 4.93:1 on white
   * clears the 3:1 non-text floor (1.4.11).
   */
  focus: {
    ring: signer.orange.dark,
    width: "2px",
    offset: "3px",
  },

  /** User analytics ▸ Template analytics ▸ Sent requests ▸ Recipient analytics. */
  tabs: {
    gap: "34px",
    marginTop: "22px",
    marginBottom: "26px",
    borderColor: contentCard.border,
    borderWidth: "1.5px",
    fontSize: "15px",
    activeColor: signer.orange.main,
    inactiveColor: signer.text.subtitle,
    activeWeight: 800,
    inactiveWeight: 600,
    indicatorWidth: "2.5px",
    paddingBottom: "14px",
  },

} as const;

/**
 * Raw media query for the narrow phone column of
 * `Reports - Mobile & Tablet.dc.html`.
 *
 * The reference draws 375 and 390 a step apart — gutters, chip gaps and the row
 * card's type all shrink by half a point on the narrow column. MUI has no
 * breakpoint between the two, so the split is expressed as a raw query and
 * applied *after* the `xs` values so it wins.
 */
export const PHONE_SM_QUERY = "@media (max-width:389.98px)";

/**
 * `xs`+`sm` and `xs` as raw `max-width` queries.
 *
 * Where a *new* style is being written, the `{ xs, sm, md }` breakpoint object
 * is clearer and used instead. These two exist for the places that override an
 * existing desktop declaration (`reportsToolbarStyles.ts`): a breakpoint
 * object's `xs` value is emitted with no media query at all, so it would land
 * on the desktop too. A `max-width` block cannot.
 */
export const COMPACT_QUERY = "@media (max-width:899.98px)";
export const PHONE_QUERY = "@media (max-width:599.98px)";

/**
 * Phone (`xs`) and tablet (`sm`) values, traced from
 * `Reports - Mobile & Tablet.dc.html`.
 *
 * Only what actually differs from the desktop tokens above lives here; anything
 * omitted is inherited, so `/reports` at `md` and up is untouched. `sm` / `std`
 * inside `phone` are the reference's 375 and 390 columns.
 */
export const reportsResponsiveTokens = {
  phone: {
    /** Content column: 14px gutters at 375, 16px at 390. */
    paddingX: { sm: "14px", std: "16px" },
    paddingTop: { sm: "14px", std: "16px" },
    paddingBottom: { sm: "32px", std: "34px" },
    /** One rhythm between the toggle, the strapline, the tabs and the list. */
    gap: "14px",

    subtitleFontSize: { sm: "12px", std: "12.5px" },
    subtitleLineHeight: 1.45,

    /** The tab row becomes a horizontally-scrolling chip rail. */
    tabsGap: { sm: "7px", std: "8px" },
  },

  tablet: {
    paddingX: "26px",
    // 22px, not 24: the gutter matches Templates at every width — that screen
    // set the house values and Reports/Insights follow it.
    paddingTop: "22px",
    paddingBottom: "40px",
    gap: "16px",

    titleFontSize: "22px",
    titleLetterSpacing: "-.01em",
    subtitleFontSize: "13px",
    subtitleMarginTop: "5px",
    headerGap: "16px",

    /** Underline tabs return, on tighter tracks than the desktop's 34px. */
    tabsGap: "20px",
    tabFontSize: "13.5px",
    tabPaddingBottom: "12px",
    tabPaddingX: "2px",
  },
} as const;

/**
 * The Insights ▸ Reports segmented control. Both `/insights` and `/reports`
 * draw it below `md`; the reference has no such switch on the desktop, so it is
 * hidden from `md` up and the side nav stays the only way across.
 */
export const analyticsToggleTokens = {
  bg: signer.border.light,
  radius: "11px",
  padding: "4px",
  gap: "4px",
  /** The tablet reference parks it at a fixed width beside the title. */
  tabletWidth: "260px",

  pillHeight: 36,
  pillRadius: "8px",
  pillFontSize: "12.5px",
  pillFontWeight: 700,

  activeBg: white,
  activeColor: signer.ink,
  activeShadow: "0 1px 3px rgba(33,21,71,.14)",
  // 4.69:1 on the #EFEDF5 track. The mock's #8A85A0 measures 3.30:1 there —
  // under the 4.5:1 WCAG 1.4.3 asks of this 12.5px label.
  idleColor: signer.text.meta,
} as const;

/** Pill tabs the reference swaps the underline tab row for on a phone. */
export const reportsChipTabTokens = {
  padding: "8px 13px",
  radius: "999px",
  fontSize: "12.5px",
  fontWeight: 700,
  activeBg: signer.ink,
  activeBorder: signer.ink,
  activeColor: white,
  idleBg: white,
  idleBorder: formField.borderWarm,
  // 5.50:1 on white; the mock's own value, kept as-is.
  idleColor: signer.text.meta,
} as const;

/**
 * Search field, selection bar and record cards — the phone/tablet stand-in for
 * `CustomTable`, which has no readable form at these widths.
 */
export const reportsRecordListTokens = {
  search: {
    bg: white,
    border: formField.borderWarm,
    radius: "11px",
    padding: { phone: "11px 13px", tablet: "12px 15px" },
    gap: "9px",
    iconSize: { phone: 15, tablet: 16 },
    fontSize: { phoneSm: "12.5px", phone: "13px", tablet: "13.5px" },
  },

  /** "Select all" line above the list, and the Download button beside it. */
  selectionBar: {
    gap: "10px",
    labelFontSize: { phoneSm: "11.5px", phone: "12px", tablet: "13px" },
    labelFontWeight: 700,
    labelColor: signer.text.meta,
    downloadHeight: 38,
    downloadPaddingX: "14px",
    downloadRadius: "9px",
    downloadFontSize: "12.5px",
    downloadGap: "7px",
    downloadIconSize: { phone: 13, tablet: 15 },
  },

  checkbox: {
    size: 18,
    radius: "5px",
    borderWidth: "1.8px",
    borderColor: "#D8CFE0",
    bg: white,
    checkedBg: inkfree.main,
    checkedBorder: signer.orange.main,
    checkColor: white,
    glyphSize: { phone: 10, tablet: 11 },
  },

  /** Phone — one card per record. */
  card: {
    bg: white,
    border: contentCard.border,
    selectedBg: "#FEF7F3",
    selectedBorder: "#F7C9AA",
    radius: "13px",
    padding: "12px 14px",
    gap: "9px",
  },

  /** Tablet — the cards close back up into one bordered list. */
  list: {
    bg: white,
    border: contentCard.border,
    radius: "14px",
    rowPadding: "14px 20px",
    rowDivider: signer.border.divider,
    rowGap: "16px",
    rowColumns: "20px minmax(0,1fr) auto",
  },

  row: {
    checkGap: "10px",
    nameFontSize: { phoneSm: "13px", phone: "13.5px", tablet: "13.5px" },
    nameFontWeight: 700,
    nameColor: signer.ink,
    subFontSize: { phoneSm: "11px", phone: "11.5px", tablet: "12px" },
    // 4.62:1 on white. The mock's #9B96AE measures 2.79:1 — well under the
    // 4.5:1 WCAG 1.4.3 asks of this size.
    subColor: "#767191",
    subMarginTop: "2px",
    pairGap: "6px",
    pairMarginTop: "8px",
  },

  pair: {
    bg: signer.surface.muted,
    color: signer.text.secondary,
    radius: "7px",
    padding: { phoneSm: "4px 8px", phone: "4px 9px", tablet: "4px 9px" },
    fontSize: { phoneSm: "10.5px", phone: "11px", tablet: "11px" },
    fontWeight: 700,
  },

  empty: {
    bg: white,
    border: contentCard.border,
    radius: "14px",
    padding: { phone: "44px 18px", tablet: "56px 24px" },
    titleFontSize: { phone: "13.5px", tablet: "15px" },
    titleColor: signer.ink,
    subFontSize: { phone: "11.5px", tablet: "12.5px" },
    subColor: "#767191",
    subMarginTop: "5px",
  },

  /**
   * The reference lists every record in one go, so it draws no pager. The real
   * report is paged ten at a time, so a compact one is kept for the case the
   * reference never shows — more than a page of rows.
   */
  pager: {
    gap: "10px",
    marginTop: "4px",
    fontSize: "12px",
    color: "#767191",
    buttonSize: 32,
    buttonRadius: "8px",
    buttonBorder: formField.borderWarm,
    buttonColor: signer.text.secondary,
    iconSize: 16,
  },
} as const;

export default reportsTokens;
